import { System } from './system';
import { Entity, EntityBuilder, EntityId, EntityOpts } from './entity';
import { Query } from './query';
import { ComponentConstructor } from './component';

export type SystemAndProps<T extends { new (...args: unknown[]): System }> = {
    system: T;
    props: ConstructorParameters<T>;
};

export class World {
    private systems: System[] = [];
    private queryRegistry: Map<ComponentConstructor, Query[]> = new Map();
    private allQueries: Array<Query> = [];
    /**
     * Queries that have only Without aspects don't appear in queryRegistry
     * under any component, so we keep them in a flat list and check them
     * on every entity-component change.
     */
    private exclusiveOnlyQueries: Array<Query> = [];
    /**
     * Monotonic tick stamped onto Query._visitedTick during
     * refreshQueriesForEntity. Lets us dedupe per-entity work across multiple
     * components without allocating a Set per build.
     */
    private _refreshTick = 0;
    private entities: Map<EntityId, Entity> = new Map();
    private deadEntities: Array<Entity> = [];
    private entitiesToBePurged: Array<Entity> = [];
    private initialized = false;

    private onRunCallbacks: Array<(delta: number) => void> = [];

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {}

    static create(): World {
        return new World();
    }

    destroy(): void {
        this.systems = [];
        this.queryRegistry.clear();
        this.allQueries = [];
        this.exclusiveOnlyQueries = [];
        this.entities.clear();
        this.deadEntities = [];
        this.entitiesToBePurged = [];
        this.initialized = false;
    }

    public getEntityById(id: EntityId) {
        return this.entities.get(id);
    }

    /**
     * Keeping track of which queries are interested in which components,
     * in order to reduce the amount of iterations when updating entity lists
     */
    addToQueryRegistry(
        key: ComponentConstructor,
        query: Query,
        registry: Map<ComponentConstructor, Query[]>
    ): void {
        if (!registry.has(key)) {
            registry.set(key, [query]);
        } else {
            registry.get(key)?.push(query);
        }
    }

    mapAspects(system: System): void {
        system.queries.forEach((query: Query) => {
            const aspects = query.aspects;
            aspects.forEach((a) => {
                this.addToQueryRegistry(
                    a.component,
                    query,
                    this.queryRegistry
                );
            });
            if (query.hasOnlyExclusiveAspects) {
                this.exclusiveOnlyQueries.push(query);
            }
            this.allQueries.push(query);
        });
    }

    addSystem<T extends { new (...args: any[]): System }>(
        systemClass: T,
        ...args: ConstructorParameters<T>
    ) {
        const systemInstance = new systemClass(...args);
        systemInstance.world = this;
        this.mapAspects(systemInstance);
        this.systems.push(systemInstance);

        return this;
    }

    /**
     * Adds a callback that is called on each world.run
     */
    onRun = (cb: (delta: number) => void) => {
        this.onRunCallbacks.push(cb);
    };

    /**
     * Run all of the world's systems
     * @param delta Optional, defaults to 1
     */
    run = (delta = 1): void => {
        if (!this.initialized) {
            this.systems.sort((a, b) => {
                const orderA = a.order ?? 0;
                const orderB = b.order ?? 0;
                return orderA - orderB;
            });
            this.systems.forEach((system) => {
                system.initialize();
            });
            this.initialized = true;
        }

        /**
         * Flush every query so that they get fresh changesets to track
         */
        this.allQueries.forEach((query) => {
            query.flushQuery();
        });

        /**
         * Run each system
         */
        this.systems.forEach((system: System) => {
            system.run(delta);
        });

        this.processGraveyard();

        this.onRunCallbacks.forEach((cb) => cb(delta));
    };

    mapEntity(entity: Entity) {
        if (this.entities.has(entity.id)) {
            throw new Error(
                `Entity with id "${entity.id}" already exists in the world.`
            );
        }
        this.entities.set(entity.id, entity);
    }

    updateRegistry(component: ComponentConstructor, entity: Entity): void {
        this.queryRegistry.get(component)?.forEach((query: Query) => {
            query.updateRegistry(entity);
        });
        for (const q of this.exclusiveOnlyQueries) {
            q.updateRegistry(entity);
        }
    }

    /**
     * Refresh every query that cares about any of the given components, exactly
     * once per query. Used by EntityBuilder.build so that queries spanning
     * multiple just-added components don't get re-checked per component.
     */
    refreshQueriesForEntity(
        entity: Entity,
        components: ComponentConstructor[]
    ): void {
        const tick = ++this._refreshTick;
        for (const c of components) {
            const queries = this.queryRegistry.get(c);
            if (!queries) continue;
            for (const q of queries) {
                if (q._visitedTick === tick) continue;
                q._visitedTick = tick;
                q.updateRegistry(entity);
            }
        }
        for (const q of this.exclusiveOnlyQueries) {
            if (q._visitedTick === tick) continue;
            q._visitedTick = tick;
            q.updateRegistry(entity);
        }
    }

    spawnEntity(opts?: EntityOpts): EntityBuilder {
        return EntityBuilder.create(this, opts);
    }

    processGraveyard() {
        this.entitiesToBePurged.forEach((entity) => {
            entity.purge();
            this.entities.delete(entity.id);
        });
        this.entitiesToBePurged = this.deadEntities.slice();
        this.deadEntities.length = 0;
    }

    markDeadEntity(entity: Entity) {
        this.deadEntities.push(entity);
    }

    /**
     * Used for testing
     */
    getSystem<T extends { new (...args: never): System }>(
        systemClass: T
    ): InstanceType<T> {
        return this.systems.find(
            (s) => s instanceof systemClass
        ) as InstanceType<T>;
    }
}
